import { AbstractControl, FormControl, ValidationErrors } from "@angular/forms";

export class PasswordValidator {
  public static readonly validations = {
    email: [
      { type: "required", message: "Input an email." },
      { type: "pattern", message: "Input a valid email." },
    ],
    password: [
      { type: "required", message: "A password is required." },
      { type: "needsLengthening", message: "Your password is too short." },
      { type: "needsShortening", message: "Your password is too long." },
      { type: "needsSymbol", message: "Your password needs a symbol." },
      { type: "needsNumber", message: "Your password needs a number." },
      {
        type: "needsLowercase",
        message: "Your password needs a lowercase letter.",
      },
      {
        type: "needsUppercase",
        message: "Your password needs an uppercase letter.",
      },
      {
        type: "needsNew",
        message: "Your new password can't be the same as the old one.",
      },
    ],
    confirm_password: [
      { type: "required", message: "Password does not match confirmation" },
      { type: "needsMatch", message: "Password does not match confirmation" },
    ],
    terms: [
      {
        type: "required",
        message: "Please read and agree to the terms of service",
      },
    ],
  };

  static check(abstractForm: AbstractControl): ValidationErrors | null {
    const form = abstractForm as FormControl;

    const password = form.value as string | null;
    if (password == null || password == "") {
      return { required: true };
    }

    // The precedence of returned rules is:
    // 1. Password too short/long
    // 2. Password meets less than 3 of the validation categories
    // 3. Password does not match confirmation

    if (password.length < 12) {
      return { needsLengthening: true };
    }
    if (password.length > 50) {
      return { needsShortening: true };
    }

    // Categories to test against, with those with more concise messages ordered at the bottom
    const categories = [
      { cond: /[!@#$%^&*(),.?":{}|<>]/, hint: { needsSymbol: true } },
      { cond: /[0-9]/, hint: { needsNumber: true } },
      { cond: /[a-z]/, hint: { needsLowercase: true } },
      { cond: /[A-Z]/, hint: { needsUppercase: true } },
    ];

    let categoriesMet = 0;
    let hint: null | object = null;
    for (const i of categories) {
      if (i.cond.test(password)) {
        categoriesMet++;
      } else {
        hint = i.hint;
      }
    }

    if (categoriesMet >= 3) {
      return null;
    } else {
      return hint;
    }
  }

  // When setting/changing a password, confirms the confirmation field matches it
  static checkConfirm(abstractForm: AbstractControl): ValidationErrors | null {
    const form = abstractForm as FormControl;

    const confirm_password = form.value as string | null;
    const password = form.parent?.get("password")?.value as string | null;

    if (password == null || confirm_password == null || password == "" || confirm_password == "") {
      return { required: true };
    }

    if (password === confirm_password) {
      return null;
    } else {
      // Password does not match the confirm field
      return { needsMatch: true };
    }
  }

  // When changing a password, makes sure the new one is not the old one and meets requirements
  static checkChange(abstractForm: AbstractControl): ValidationErrors | null {
    const form = abstractForm as FormControl;

    const newPassword = form.value as string | null;
    const oldPassword = form.parent?.get("old_password")?.value as string | null;

    if (newPassword == null || oldPassword == null || newPassword == "" || oldPassword == "") {
      return { required: true };
    }

    if (newPassword === oldPassword) {
      return { needsNew: true };
    } else {
      // Delegate to checking the formal requirements
      return this.check(form);
    }
  }
}
